import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Collapse,
  Heading,
  HStack,
  IconButton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import LineRequestForm, { nominatedLeads } from "./LineRequestForm";
import LinesView from "./LinesView";
import SignDownPanel from "./SignDownPanel";
import SignedDownView from "./SignedDownView";
import { isSignedDown } from "./placingUtils";
import { getPlacementLines } from "../../services/PlacingAPI";
import { showApiErrorToast } from "../../utils/showApiToast";

/**
 * Broker: the Order stage (steps 13–15). Appears once a lead is nominated:
 * request lead and follower lines, and see the lines and offers that come
 * back. Collapsed until the first line request.
 */
const OrderSection = ({ submission, placement, comparison, onChanged, showSigning = true, defaultOpen = false }) => {
  const toast = useToast();
  const placementId = submission?.placementId;
  const [lines, setLines] = useState(null);
  const [open, setOpen] = useState(null); // null: follow the default

  const loadLines = useCallback(async () => {
    if (!placementId) return;
    try {
      setLines(await getPlacementLines(placementId));
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't load lines" });
    }
  }, [placementId, toast]);

  useEffect(() => {
    loadLines();
  }, [loadLines, comparison]);

  const hasLead = nominatedLeads(comparison).length > 0;
  const signedDown = isSignedDown(placement);
  const hasLines = (lines?.rows || []).length > 0;
  if (!hasLead && !hasLines) return null;

  const isOpen = open ?? (defaultOpen || hasLines);

  const afterSending = async () => {
    await loadLines();
    await onChanged(); // the placement moves to Order on the first line request
  };

  return (
    <Box borderWidth="1px" borderRadius="md" p={4} bg="whiteAlpha.800">
      <HStack cursor="pointer" onClick={() => setOpen(!isOpen)} spacing={2}>
        <IconButton size="xs" variant="ghost" aria-label={isOpen ? "Collapse" : "Expand"} icon={isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />} />
        <Heading size="sm">Order</Heading>
        <Text fontSize="xs" color="gray.500">
          {signedDown
            ? "Signed down"
            : hasLines
            ? `${lines.lineRequests ?? lines.rows.length} line request(s)`
            : "Lead nominated: request lines from the lead and followers"}
        </Text>
      </HStack>
      <Collapse in={isOpen} animateOpacity>
        <VStack align="stretch" spacing={5} mt={4}>
          {/* Requesting more lines stops once the risk is closed */}
          {hasLead && !signedDown && (
            <Tabs size="sm" variant="enclosed" isLazy>
              <TabList>
                <Tab>Request lead line(s)</Tab>
                <Tab>Request follower lines</Tab>
              </TabList>
              <TabPanels borderWidth="1px" borderTopWidth={0} borderRadius="0 0 md md">
                <TabPanel>
                  <LineRequestForm role="lead" placementId={placementId} submission={submission} comparison={comparison} lines={lines} onSent={afterSending} />
                </TabPanel>
                <TabPanel>
                  <LineRequestForm role="follower" placementId={placementId} submission={submission} comparison={comparison} lines={lines} onSent={afterSending} />
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
          <LinesView lines={lines} />
          {showSigning &&
            (signedDown ? (
              <SignedDownView placement={placement} />
            ) : (
              placement?.placingStageCode === "order" && hasLines && (
                <SignDownPanel placement={placement} lines={lines} comparison={comparison} onSigned={afterSending} />
              )
            ))}
        </VStack>
      </Collapse>
    </Box>
  );
};

export default OrderSection;
